import { FormGroup, InputLabel } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./country-code-phone-numner-input.scss";
import { extractCountryAndNumber } from "../../../utils/utils";
import { getExampleNumber, CountryCode, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
interface CountryCodeWithPhoneNumberProps {
  optional?: boolean;
  fieldName: string;
  label?: string;
  countryCodeEditable?: boolean;
  onChange?: (value: string, country: any, e: any, formattedValue: any) => void;
  enableSearch?: boolean;
  placeholder?: string;
  disableSearchIcon?: boolean;
  value: string;
  defaultCountry?: string | undefined | number;
  // countryIso?: string;
  disabled?: boolean;
  key?: any;
  onKeyDown?: any;
  onBlur?: () => void;
  isRequired?: boolean;
  rightHelper?: React.ReactNode;
  showExampleFormat?: boolean;
}

const placeHolder: any = {
  1: "20px",
  2: "60px",
  3: "70px",
  4: "75px",
  5: "80px",
};

const getIsoFromValue = (val: string, fallback: string): string => {
  if (!val) return fallback;
  try {
    const parsed = parsePhoneNumberFromString(val);
    if (parsed?.country) return parsed.country.toLowerCase();
  } catch {}
  return fallback;
};

const CountryCodeWithPhoneNumber: React.FC<CountryCodeWithPhoneNumberProps> = ({
  optional = false,
  fieldName,
  label,
  placeholder,
  enableSearch = true,
  countryCodeEditable = false,
  disableSearchIcon = true,
  onChange,
  value,
  defaultCountry = "us",
  //countryIso,
  disabled,
  key,
  onKeyDown,
  onBlur,
  isRequired = false,
  rightHelper,
  showExampleFormat = true,
}) => {
  const countePhoneObj = extractCountryAndNumber(value);
  const countryCodeLength = countePhoneObj?.countryCode?.length;
  const isEmpty = value.trim() === "" || !countePhoneObj?.number?.length;
  const fallbackIso = typeof defaultCountry === "string" ? defaultCountry : "us";
  const [isoCode, setIsoCode] = useState(() => getIsoFromValue(value, fallbackIso));
  const [localValue, setLocalValue] = useState<string>(
    value ? value.replace(/[^\d+]/g, "") : ""
  );
  const isoCodeRef = useRef<string>(getIsoFromValue(value, fallbackIso));
  const isInternalChange = useRef(false);
  const getCallingCode = (iso: string): string => {
    try {
      return String(getCountryCallingCode(iso.toUpperCase() as CountryCode));
    } catch {
      return "1";
    }
  };
  const callingCodeRef = useRef<string>(
    getCallingCode(getIsoFromValue(value, fallbackIso))
  );

  // Sync localValue when parent resets or changes value externally
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const cleaned = value ? value.replace(/[^\d+]/g, "") : "";
    setLocalValue(cleaned);
    // Update refs for max-length enforcement
    const detectedIso = cleaned ? getIsoFromValue(value, fallbackIso) : fallbackIso;
    isoCodeRef.current = detectedIso;
    callingCodeRef.current = getCallingCode(detectedIso);
    // Only reset the isoCode state (flag) when value is fully cleared
    if (!cleaned) {
      setIsoCode(fallbackIso);
    }
    // When a non-empty value loads externally (e.g., API response), we do NOT
    // call setIsoCode — the library auto-detects the country from the +XX prefix
    // in the value. Changing country prop simultaneously with value causes the
    // library to reset the displayed number to just the dial code.
  }, [value]);
  const getPhoneExample = (isoCode?: string) => {
    try {
      if (!isoCode) return "";

      const example = getExampleNumber(
        isoCode.toUpperCase() as CountryCode,
        examples
      );

      return example
        ? `Eg: ${example.formatInternational()}`
        : "";
    } catch {
      return "";
    }
  };
  const getMaxLength = (isoCode: string) => {
    try {
      const example = getExampleNumber(
        isoCode.toUpperCase() as CountryCode,
        examples
      );

      return example?.nationalNumber?.length || 15;
    } catch {
      return 15;
    }
  };
  const exampleText =
    typeof isoCode === "string"
      ? getPhoneExample(isoCode)
      : "";
  const countryForInput = typeof isoCode === "string" ? isoCode : fallbackIso;
  return (
    <FormGroup>
      {label && (
        <InputLabel
          required={!optional}
          htmlFor={fieldName}
          className="phone-label"
        >
          {label}
        </InputLabel>
      )}
      <div
        className={`phone-input-wrapper ${isRequired ? "phone-input-wrapper-required" : ""}`}
      >
        {isEmpty && (
          <span
            className="phone-placeholder"
            style={{
              left: placeHolder?.[countryCodeLength] || "60px",
            }}
          >
            {placeholder}
          </span>
        )}
        <PhoneInput
          country={countryForInput}
          value={localValue}
          disableCountryGuess={false}
          countryCodeEditable={countryCodeEditable}
          onChange={(rawValue: string, country: any, e: any, formattedValue: any) => {
            const iso = country?.countryCode;
            const callingCode = getCallingCode(iso);

            // rawValue is pure digits without '+' (e.g. "918682911234" for IN)
            // Strip the calling code prefix to isolate the national number
            const nationalNumber = rawValue.startsWith(callingCode)
              ? rawValue.slice(callingCode.length)
              : "";

            // If rawValue doesn't start with the calling code, user has backspaced
            // into the dial code — snap back to just the calling code.
            const userDeletedIntoCode = rawValue.length > 0 && !rawValue.startsWith(callingCode);

            // Keep current iso when national number is empty (dropdown selection or
            // backspace to dial-code only). Only truly reset when rawValue is empty.
            const activeIso = rawValue.length === 0 ? fallbackIso : iso;

            setIsoCode(activeIso);
            isoCodeRef.current = activeIso;

            const activeCallingCode = getCallingCode(activeIso);
            callingCodeRef.current = activeCallingCode;

            const nationalMax = getMaxLength(activeIso);
            const trimmedNational = nationalNumber.slice(0, nationalMax);

            const newValue = trimmedNational.length > 0
              ? `+${activeCallingCode}${trimmedNational}`
              : "";

            isInternalChange.current = true;
            // If user deleted into the dial code, or cleared everything, show just the dial code digits
            const displayValue = (userDeletedIntoCode || rawValue.length === 0)
              ? activeCallingCode
              : (newValue ? newValue : activeCallingCode);
            setLocalValue(displayValue);
            onChange?.(newValue, { ...country, countryCode: activeIso, dialCode: activeCallingCode }, e, formattedValue);
          }}
          enableSearch={enableSearch}
          disableSearchIcon={disableSearchIcon}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          inputProps={{
            name: fieldName,
            required: !optional,
            autoComplete: "off",
            onKeyDown: (e: any) => {
              if (/^\d$/.test(e.key)) {
                const input = e.target as HTMLInputElement;
                const totalDigits = input.value.replace(/\D/g, '').length;
                const nationalMax = getMaxLength(isoCodeRef.current);
                if (totalDigits >= callingCodeRef.current.length + nationalMax) {
                  e.preventDefault();
                  return;
                }
              }
              onKeyDown?.(e);
            },
          }}
          inputStyle={{
            width: "100%",
            borderRadius: "6px",
            border: "1px solid #ccc",
            paddingLeft: "48px",
          }}
          buttonStyle={{
            border: "none",
            backgroundColor: "transparent",
          }}
          dropdownStyle={{
            position: "absolute",
            textAlign: "left",
          }}
          inputClass="phone-input"
        />
      </div>

      {(showExampleFormat && exampleText || rightHelper) && (
        <div className="phone-helper-row">
          {showExampleFormat && exampleText ? <p className="phone-hint">{exampleText}</p> : <span />}
          {rightHelper ? <div className="phone-helper-right">{rightHelper}</div> : null}
        </div>
      )}
    </FormGroup>
  );
};

export default CountryCodeWithPhoneNumber;
